/**
 * homeMoney.js — the Home page's admin money boxes, each with its line items.
 *
 * Every dollar of an active job lands in exactly one box (decided by Cyprian
 * 2026-10-11), so the boxes can be read side by side without double counting:
 *
 *   PIPELINE     intake / quoted jobs: revenue minus anything already collected.
 *                A deposit invoice on a quote nobody has accepted stays here.
 *   BOOKED       confirmed → invoiced jobs: revenue not yet invoiced. Includes
 *                'delivered', which the old status-only formula dropped from
 *                every box until someone clicked "invoiced".
 *   OUTSTANDING  open invoice balances, except invoices on jobs still at intake
 *                or quoted. The old version summed the full revenue of invoiced
 *                jobs, so deposits already paid were reported as still owed.
 *   (collected)  money already paid sits in none of the three.
 *
 * Unlike lib/jobMoney.js this module reads invoices: Outstanding is a
 * receivable, and Booked must subtract exactly what Outstanding already holds.
 *
 * OWED TO CREW — non-owner crew pay for finished work that has not been paid.
 *   The job's pay split (calculator snapshot) says what each person earns and
 *   the Payroll grid (crew_earnings) says what was paid: paying a person marks
 *   the grid lines that payment covered. The ledger's paid flag is not used —
 *   Close & pay flips every earned ledger row for the person, whatever the
 *   payment covered (PA + SCRIPTY: $525 flagged paid, never paid). Decided by
 *   Cyprian 2026-10-13: "Pay split is the source of truth".
 *   Active jobs: per person, the split's share in buckets whose trigger has
 *     fired (prod at post_production, post at delivered, originator at paid)
 *     minus what the Payroll grid paid them on this job. The status sweeper
 *     moves jobs with raw SQL and skips the ledger, so status decides, not
 *     ledger state; a bucket the ledger already shows earned stays owed after
 *     a backward status move.
 *   Archived jobs: settled unless the Payroll grid agrees. Only the part of
 *     the split's unpaid share that the grid still shows as an unpaid line
 *     counts. What the ledger also shows unpaid is listed, not counted.
 *   Still to come: the split's unfired buckets on booked jobs, less any grid
 *   payment beyond the fired share.
 *   Crew paid in lump sums with no job on the grid line (Shay's "Payroll")
 *   can't be matched to a job, so their split share stays owed.
 *   Mileage and admin pool are paid per pay period outside the ledger and are
 *   not included.
 */
import { isBooked, isOwner, localDateStr, LEDGER_BUCKET } from './jobMoney';
import { normalizeSnapshot, getAllEarnings } from './calcEngine';
import { ROLE_TRIGGERS, isStatusAtOrPast } from './payoutEngine';

const PRE_CONFIRM = new Set(['intake', 'quoted']);
const TO_BILL = new Set(['confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced']);
const CLOSED_INVOICE = new Set(['paid', 'void', 'draft']);
const BUCKET_TRIGGER = { orig: ROLE_TRIGGERS.originator, prod: ROLE_TRIGGERS.prod_lead, post: ROLE_TRIGGERS.post_lead };

const num = (v) => parseFloat(v) || 0;
const r2 = (n) => Math.round(n * 100) / 100;
const personKey = (p) => (p || '').trim().toLowerCase();
const sum = (items) => r2(items.reduce((s, i) => s + i.amount, 0));

function jsonArray(v) {
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return []; }
  }
  return Array.isArray(v) ? v : [];
}

function invoiceJobIds(inv) {
  const ids = new Set();
  if (inv.jobId) ids.add(inv.jobId);
  jsonArray(inv.jobIds).forEach((id) => id && ids.add(id));
  return [...ids];
}

/**
 * [job, weight] pairs. A bulk invoice's share for a job is its line_items[]
 * entry (BulkInvoiceModal bills revenue minus any paid deposit), the same rule
 * as server/lib/jobInvoiceStatus.js. Only what no line item accounts for is
 * split by revenue across the jobs without one.
 */
function invoiceShares(inv, jobsById) {
  const linked = invoiceJobIds(inv).map((id) => jobsById.get(id)).filter(Boolean);
  if (linked.length <= 1) return linked.map((j) => [j, 1]);
  const amount = num(inv.amount);
  const byLine = new Map();
  for (const li of jsonArray(inv.lineItems)) {
    if (li?.jobId) byLine.set(li.jobId, (byLine.get(li.jobId) || 0) + num(li.amount));
  }
  const withLine = linked.filter((j) => byLine.has(j.id));
  const rest = linked.filter((j) => !byLine.has(j.id));
  const out = amount > 0 ? withLine.map((j) => [j, byLine.get(j.id) / amount]) : [];
  const leftover = 1 - out.reduce((s, [, w]) => s + w, 0);
  if (rest.length && leftover > 0) {
    const revTotal = rest.reduce((s, j) => s + num(j.revenue), 0);
    rest.forEach((j) => out.push([j, leftover * (revTotal > 0 ? num(j.revenue) / revTotal : 1 / rest.length)]));
  }
  return out.length ? out : linked.map((j) => [j, 1 / linked.length]);
}

function invoiceCollected(inv) {
  if (inv.status === 'paid') return num(inv.amount);
  return Math.min(num(inv.paidAmount), num(inv.amount));
}

function lastShootDate(job) {
  const dates = (job.shootDates?.length ? job.shootDates : [job.shootDate]).map(localDateStr).filter(Boolean).sort();
  return dates[dates.length - 1] || null;
}

// useJobs maps the `client` column to clientName.
function jobRef(job) {
  const client = job.clientName || job.client || '';
  return { jobId: job.id, title: job.title || client || 'Untitled', client, status: job.status };
}

// ── Crew ────────────────────────────────────────────────────────────────────

/**
 * What one job still owes its non-owner crew, split into work already done
 * (`now`) and work whose trigger has not fired (`later`). People are keyed by
 * display name. `gridPaid` is personKey → what the Payroll grid paid on this job.
 */
export function jobCrewOwed(job, ledgerRows = [], gridPaid = new Map()) {
  const due = new Map(); // personKey → { name, now, later }
  const add = (name, which, v) => {
    const k = personKey(name);
    if (!due.has(k)) due.set(k, { name: name.trim(), now: 0, later: 0 });
    due.get(k)[which] += v;
  };

  // Earned or paid: the bucket's trigger fired at some point.
  const rows = ledgerRows.filter((r) => r.state !== 'forfeited' && !isOwner(r.person));
  const fired = (r) => r.state === 'earned' || r.state === 'paid' || !!r.paidAt;

  if (job.status === 'void') {
    // A voided job's earned rows stay payable, but nothing new fires and the
    // split no longer describes owed work.
    for (const r of rows) if (fired(r)) add(r.person, 'now', num(r.amount));
  } else {
    const ledgerFired = new Set(rows.filter(fired).map((r) => `${LEDGER_BUCKET[r.role]}|${personKey(r.person)}`));
    for (const e of getAllEarnings(normalizeSnapshot(job.calcSnapshot))) {
      if (isOwner(e.name) || !BUCKET_TRIGGER[e.role]) continue;
      const done = isStatusAtOrPast(job.status, BUCKET_TRIGGER[e.role]) || ledgerFired.has(`${e.role}|${personKey(e.name)}`);
      add(e.name, done ? 'now' : 'later', e.earned || 0);
    }
  }

  // Grid payments cover finished work first; any excess was paid in advance.
  const now = new Map();
  const later = new Map();
  for (const [k, d] of due) {
    const paid = gridPaid.get(k) || 0;
    const n = r2(d.now - paid);
    const l = r2(d.later - Math.max(0, paid - d.now));
    if (n > 0) now.set(d.name, n);
    if (l > 0) later.set(d.name, l);
  }
  return { now, later };
}

function peopleList(m) {
  return [...m.entries()].map(([name, amount]) => ({ name, amount })).sort((a, b) => b.amount - a.amount);
}

function computeCrew(jobs, ledger, crewEarnings) {
  const ledgerByJob = new Map();
  for (const r of ledger) {
    if (!r.jobId) continue;
    if (!ledgerByJob.has(r.jobId)) ledgerByJob.set(r.jobId, []);
    ledgerByJob.get(r.jobId).push(r);
  }
  // Payroll grid (crew_earnings), non-owner, by job and person: unpaid lines
  // and lines a payment covered.
  const payrollByJob = new Map();
  const paidByJob = new Map();
  for (const c of crewEarnings) {
    if (!c.jobId || isOwner(c.person)) continue;
    const byJob = c.paidAt ? paidByJob : payrollByJob;
    if (!byJob.has(c.jobId)) byJob.set(c.jobId, new Map());
    const m = byJob.get(c.jobId);
    m.set(personKey(c.person), (m.get(personKey(c.person)) || 0) + num(c.amount));
  }
  const payrollTotal = (jobId) => r2([...(payrollByJob.get(jobId)?.values() || [])].reduce((s, v) => s + v, 0));

  const items = [];      // counted now
  const upcoming = [];   // still to come
  const notCounted = []; // archived, split and ledger say unpaid, Payroll disagrees

  for (const j of jobs) {
    const rows = ledgerByJob.get(j.id) || [];
    if (j.status === 'archived') {
      // Count the split's unpaid share only as far as the grid still has an
      // unpaid line for it. List the rest where the ledger also shows it
      // unpaid; work paid before the grid existed has neither. Void history
      // keeps the void rule (same test as server/lib/trash.js).
      const { now } = jobCrewOwed(j.archivedFromVoid ? { ...j, status: 'void' } : j, rows, paidByJob.get(j.id));
      const ledgerOpen = new Set(rows.filter((r) => r.state === 'earned' && !r.paidAt).map((r) => personKey(r.person)));
      const agreed = [];
      const disputed = [];
      for (const [name, amount] of now) {
        const k = personKey(name);
        const both = r2(Math.min(amount, payrollByJob.get(j.id)?.get(k) || 0));
        if (both > 0) agreed.push({ name, amount: both });
        if (r2(amount - both) > 0 && ledgerOpen.has(k)) disputed.push({ name, amount: r2(amount - both) });
      }
      if (agreed.length) items.push({ ...jobRef(j), people: agreed, amount: sum(agreed), archived: true });
      if (disputed.length) notCounted.push({ ...jobRef(j), people: disputed, amount: sum(disputed) });
      continue;
    }
    const { now, later } = jobCrewOwed(j, rows, paidByJob.get(j.id));
    if (now.size) {
      const people = peopleList(now);
      const amount = sum(people);
      const payroll = payrollTotal(j.id);
      items.push({ ...jobRef(j), people, amount, payroll: payroll !== amount ? payroll : null });
    }
    if (later.size && isBooked(j)) {
      const people = peopleList(later);
      upcoming.push({ ...jobRef(j), people, amount: sum(people) });
    }
  }

  const byPerson = new Map();
  for (const it of items) for (const p of it.people) byPerson.set(p.name, r2((byPerson.get(p.name) || 0) + p.amount));
  const desc = (a, b) => b.amount - a.amount;
  return {
    total: sum(items),
    items: items.sort(desc),
    people: peopleList(byPerson),
    upcoming: upcoming.sort(desc),
    upcomingTotal: sum(upcoming),
    notCounted: notCounted.sort(desc),
    notCountedTotal: sum(notCounted),
  };
}

// ── All four boxes ──────────────────────────────────────────────────────────

/**
 * @param {object[]} jobs          useJobs() rows (camelCase)
 * @param {object[]} invoices      useInvoices() rows (camelCase)
 * @param {object[]} ledger        job_earnings_ledger rows (camelCase)
 * @param {object[]} crewEarnings  crew_earnings rows (camelCase)
 * @param {string}   today         'YYYY-MM-DD', viewer's local date
 */
export function computeHomeMoney({ jobs = [], invoices = [], ledger = [], crewEarnings = [], today }) {
  const jobsById = new Map(jobs.map((j) => [j.id, j]));
  const billed = new Map();
  const collected = new Map();
  const add = (m, id, v) => m.set(id, (m.get(id) || 0) + v);

  const outItems = [];
  const outExcluded = [];
  for (const inv of invoices) {
    if (inv.status === 'void') continue;
    const shares = invoiceShares(inv, jobsById);
    // An 'extra' invoice is billed on top of revenue (InvoiceForm), so it is a
    // receivable but never covers any of the job's revenue.
    if (inv.invoiceType !== 'extra') {
      for (const [j, w] of shares) {
        add(billed, j.id, num(inv.amount) * w);
        add(collected, j.id, invoiceCollected(inv) * w);
      }
    }
    if (CLOSED_INVOICE.has(inv.status)) continue;
    const balance = r2(Math.max(0, num(inv.amount) - num(inv.paidAmount)));
    if (balance <= 0) continue;
    const job = shares[0]?.[0];
    const due = localDateStr(inv.dueDate);
    const item = {
      invoiceId: inv.id,
      number: inv.number || '—',
      client: inv.client || job?.clientName || job?.client || '',
      title: job?.title || '',
      jobId: job?.id || null,
      jobStatus: job?.status || null,
      type: inv.invoiceType || null,
      dueDate: due,
      daysOverdue: due && today && due < today ? Math.round((new Date(`${today}T12:00`) - new Date(`${due}T12:00`)) / 86400000) : 0,
      amount: balance,
    };
    (shares.some(([j]) => PRE_CONFIRM.has(j.status)) ? outExcluded : outItems).push(item);
  }

  const pipeItems = [];
  const bookItems = [];
  for (const j of jobs) {
    // Status only: archived_from_void is never cleared when a job is revived,
    // and both branches below exclude 'archived' anyway.
    if (j.status === 'void') continue;
    const rev = num(j.revenue);
    if (PRE_CONFIRM.has(j.status)) {
      const less = r2(collected.get(j.id) || 0);
      const amount = r2(Math.max(0, rev - less));
      const shoot = lastShootDate(j);
      if (amount > 0) pipeItems.push({ ...jobRef(j), revenue: rev, less, amount, shootDate: shoot, stale: !!(shoot && today && shoot < today) });
    } else if (TO_BILL.has(j.status)) {
      const less = r2(billed.get(j.id) || 0);
      const amount = r2(Math.max(0, rev - less));
      if (amount > 0) bookItems.push({ ...jobRef(j), revenue: rev, less, amount });
    }
  }

  const desc = (a, b) => b.amount - a.amount;
  return {
    pipeline: { total: sum(pipeItems), items: pipeItems.sort(desc) },
    booked: { total: sum(bookItems), items: bookItems.sort(desc) },
    outstanding: {
      total: sum(outItems),
      items: outItems.sort((a, b) => b.daysOverdue - a.daysOverdue || b.amount - a.amount),
      excluded: outExcluded,
      excludedTotal: sum(outExcluded),
    },
    crew: computeCrew(jobs, ledger, crewEarnings),
  };
}
